import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useExperience } from '../../contexts/ExperienceContext';
import { usageLevels, usageResponse } from '../../data/journeys';
import { brand } from '../../data/palette';
import { products } from '../../data/products';
import { Advance } from '../ui/Advance';
import { PillChoices } from '../ui/PillChoices';
import { Eyebrow, Stage, StageTitle } from '../ui/Stage';
const litCount: Record<string, number> = {
  one: 1,
  two: 2,
  all: 3
};

/** Depth of current use, framed as discovery rather than deficiency. */
export function UsageStage() {
  const {
    signals,
    update,
    next,
    direction
  } = useExperience();
  const lit = signals.usage ? litCount[signals.usage] : 0;
  const accent = brand.easyMatch;
  return <Stage direction={direction} className="min-h-screen py-28 sm:py-32" labelledBy="usage-title" data-id="element-359" data-mp="30mh6nrly8nr1">
      <div className="grid gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:items-center lg:gap-20" data-id="element-360" data-mp="upv0ewgv05cx">
        <div data-id="element-361" data-mp="11q0osl8w6tpp">
          <Eyebrow accent={accent} data-id="element-362" data-mp="11pqtci52vucm">Already using Filtek</Eyebrow>
          <StageTitle id="usage-title" size="display-sm" className="mt-7 max-w-lg" data-id="element-363" data-mp="11nxmb1qjz843">
            How much of Filtek are you actually using?
          </StageTitle>

          <div className="mt-10" data-id="element-364" data-mp="1bese0lmkizwx">
            <PillChoices choices={usageLevels} selected={signals.usage} accent={accent} size="lg" onSelect={choice => update({
            usage: choice.id
          })} data-id="element-365" data-mp="1bhwlbrvbybrt" />
          </div>

          <AnimatePresence mode="wait" data-id="element-366" data-mp="dsuzt0f81gqs">
            {signals.usage && <motion.div key={signals.usage} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }} className="mt-10 flex flex-col gap-9" data-id="element-367" data-mp="sp2i1v72k83f">
                <p className="max-w-lg text-lg font-light leading-relaxed text-enamel/85" data-id="element-368" data-mp="1z4mpayr8ogf2">
                  {usageResponse[signals.usage]}
                </p>
                <Advance label="Keep going" onClick={next} accent={accent} data-id="element-369" data-mp="2b763ixr8ze25" />
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* The family as three members and a cousin */}
        <div className="space-y-2.5" data-id="element-370" data-mp="2l654du5ru5el">
          {products.map((product, index) => {
          const isCousin = product.role === 'cousin';
          const isLit = !isCousin && index < lit;
          return <motion.div key={product.id} className={`relative overflow-hidden border px-5 py-4 ${isCousin ? 'border-dashed' : 'border-enamel/10'}`} style={isCousin ? {
            borderColor: `${product.accent}55`
          } : undefined} initial={false} animate={{
            opacity: isLit ? 1 : isCousin ? 0.7 : 0.4
          }} transition={{
            duration: 0.6,
            delay: index * 0.09
          }} data-id="element-371" data-mp="1shzg4xfidya0" data-mpi={index}>
                <motion.span className="absolute inset-0 origin-left" style={{
              backgroundColor: product.accent
            }} initial={false} animate={{
              scaleX: isLit ? 1 : 0,
              opacity: isLit ? 0.16 : 0
            }} transition={{
              duration: 0.8,
              delay: index * 0.09,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-372" data-mp="3mzye3ps9t207" />
                <div className="relative flex items-center justify-between gap-4" data-id="element-373" data-mp="342ng08idkemc">
                  <span className="text-base font-normal text-enamel" data-id="element-374" data-mp="2zbnip25cfujb">{product.name}</span>
                  <span className="label-mono shrink-0 text-[9px]" style={{
                color: isCousin ? product.accent : 'var(--mute)'
              }} data-id="element-375" data-mp="2v9tl52qg36nd">
                    {isCousin ? 'Cousin' : product.descriptor}
                  </span>
                </div>
              </motion.div>;
        })}
          <p className="pt-3 text-xs font-light text-mute" data-id="element-376" data-mp="2gduezxmwbgkv">
            Three members and a cousin. That is the whole family.
          </p>
        </div>
      </div>
    </Stage>;
}